/**
 * SEO utility functions for dynamic meta tag management
 * Uses direct DOM manipulation for simplicity and avoids external dependencies
 */

const getHead = (): HTMLHeadElement | null => {
  if (typeof document === 'undefined') return null;
  return document.head || document.getElementsByTagName('head')[0];
};

const ensureElement = <T extends HTMLElement>(
  selector: string,
  create: () => T
): T => {
  if (typeof document === 'undefined') {
    return create();
  }

  const existing = document.querySelector(selector) as T | null;
  if (existing) return existing;

  const element = create();
  const head = getHead();
  if (head) {
    head.appendChild(element);
  }
  return element;
};

interface MetaTagsConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterUrl?: string;
  twitterImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  keywords?: string | string[];
  robots?: string; // e.g., "index,follow" or "noindex,follow"
}

export const DEFAULT_OG_IMAGE = 'https://explanatum.co.uk/images/Explanatum.png';

/**
 * Updates meta tags for SEO optimization
 * Only includes public information, never user data
 * @param config Configuration object for meta tags
 */
export const updateMetaTags = (config: MetaTagsConfig): void => {
  if (typeof document === 'undefined') return;

  document.title = config.title;

  const metaDescription = ensureElement('meta[name="description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    return meta;
  });
  metaDescription.setAttribute('content', config.description);

  const ogUrl = ensureElement('meta[property="og:url"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:url');
    return meta;
  });
  if (config.ogUrl || config.canonicalUrl) {
    ogUrl.setAttribute('content', config.ogUrl || (config.canonicalUrl as string));
  }

  const ogTitle = ensureElement('meta[property="og:title"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    return meta;
  });
  ogTitle.setAttribute('content', config.ogTitle || config.title);

  const ogDescription = ensureElement('meta[property="og:description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:description');
    return meta;
  });
  ogDescription.setAttribute('content', config.ogDescription || config.description);

  const ogType = ensureElement('meta[property="og:type"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:type');
    return meta;
  });
  ogType.setAttribute('content', config.ogType || 'website');

  const ogSiteName = ensureElement('meta[property="og:site_name"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:site_name');
    return meta;
  });
  ogSiteName.setAttribute('content', 'Explanatum');

  if (config.ogImage || config.twitterImage) {
    const ogImage = ensureElement('meta[property="og:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      return meta;
    });
    ogImage.setAttribute('content', config.ogImage || (config.twitterImage as string));
  }

  const twitterCard = ensureElement('meta[name="twitter:card"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'twitter:card');
    return meta;
  });
  twitterCard.setAttribute('content', config.twitterCard || 'summary_large_image');

  const twitterUrl = ensureElement('meta[name="twitter:url"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'twitter:url');
    return meta;
  });
  if (config.twitterUrl || config.ogUrl || config.canonicalUrl) {
    twitterUrl.setAttribute('content', config.twitterUrl || config.ogUrl || (config.canonicalUrl as string));
  }

  const twitterTitle = ensureElement('meta[name="twitter:title"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'twitter:title');
    return meta;
  });
  twitterTitle.setAttribute('content', config.twitterTitle || config.ogTitle || config.title);

  const twitterDescription = ensureElement('meta[name="twitter:description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'twitter:description');
    return meta;
  });
  twitterDescription.setAttribute('content', config.twitterDescription || config.ogDescription || config.description);

  if (config.twitterImage || config.ogImage) {
    const twitterImage = ensureElement('meta[name="twitter:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:image');
      return meta;
    });
    twitterImage.setAttribute('content', config.twitterImage || (config.ogImage as string));
  }

  if (config.canonicalUrl) {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.setAttribute('href', config.canonicalUrl);
    } else {
      const canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', config.canonicalUrl);
      const head = getHead();
      head?.appendChild(canonicalLink);
    }
  }

  if (typeof config.robots === 'string') {
    const robotsMeta = ensureElement('meta[name="robots"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      return meta;
    });
    robotsMeta.setAttribute('content', config.robots);
  }

  if (config.keywords) {
    const keywordsMeta = ensureElement('meta[name="keywords"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'keywords');
      return meta;
    });
    const keywords = Array.isArray(config.keywords) ? config.keywords.join(', ') : config.keywords;
    keywordsMeta.setAttribute('content', keywords);
  }
};

/**
 * Adds structured data (JSON-LD) to the page
 * Only includes public information, never user data
 * @param data Structured data object
 * @param id Optional ID for the script tag
 */
export const addStructuredData = (data: object, id?: string): void => {
  if (typeof document === 'undefined') return;

  // Remove existing structured data with the same ID if present
  if (id) {
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }
  }
  
  // Create new script element
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  if (id) {
    script.id = id;
  }
  
  // Add to document head
  const head = getHead();
  head?.appendChild(script);
};

interface SoftwareApplicationLdConfig {
  name: string;
  description: string;
  url: string;
  category?: string;
  price?: string;
  priceCurrency?: string;
}

export const buildSoftwareApplicationLd = (config: SoftwareApplicationLdConfig) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: config.name,
  description: config.description,
  applicationCategory: config.category ?? 'BusinessApplication',
  operatingSystem: 'Web',
  url: config.url,
  offers: {
    '@type': 'Offer',
    price: config.price ?? '0',
    priceCurrency: config.priceCurrency ?? 'GBP'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Explanatum',
    url: 'https://explanatum.co.uk'
  }
});

interface ModuleSeoConfig {
  title: string;
  description: string;
  canonicalUrl: string;
  keywords: string | string[];
  structuredDataId: string;
  softwareName: string;
  softwareCategory?: string;
}

export const applyModuleSeo = (config: ModuleSeoConfig): void => {
  updateMetaTags({
    title: config.title,
    description: config.description,
    canonicalUrl: config.canonicalUrl,
    ogTitle: config.title,
    ogDescription: config.description,
    ogUrl: config.canonicalUrl,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterTitle: config.title,
    twitterDescription: config.description,
    twitterUrl: config.canonicalUrl,
    twitterImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image',
    keywords: config.keywords,
    robots: 'index,follow'
  });

  addStructuredData(
    buildSoftwareApplicationLd({
      name: config.softwareName,
      description: config.description,
      url: config.canonicalUrl,
      category: config.softwareCategory
    }),
    config.structuredDataId
  );
};
